import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  FormErrorMessage,
  Grid,
  SimpleGrid,
  HStack,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Checkbox,
  CheckboxGroup,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  Text,
  Heading,
  Divider,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import { MdDelete, MdAdd, MdMap } from "react-icons/md";
import { deleteProviderAndRateCard, fetchProviderCoverageAreas } from '../Action/action';

const AMBULANCE_TYPES = ["BLS(EECO/Bolero)", "BLS(Tempo Traveller/ Winger)", "ACLS(Tempo Traveller/ Winger)", "NICU/NALS(Tempo Traveller/Winger)", "Hearse-Van", "Air-Ambulance", "Others"];
const MANPOWER_OPTIONS = ['Helper', 'Paramedic', 'Doctor'];
const EQUIPMENT_OPTIONS = ['Stretcher', 'Scoop Stretcher', 'Wheelchair', 'Spine board', 'Immobilizer'];
const COMM_OPTIONS = ['Whatsapp', 'Call', 'SMS', 'Email'];
const PAYMENT_OPTIONS = ['UPI', 'Bank Transfer', 'Cash', 'Card'];

const defaultFleet: Record<string, number> = {
  "BLS(EECO/Bolero)": 0,
  "BLS(Tempo Traveller/ Winger)": 0,
  "ACLS(Tempo Traveller/ Winger)": 0,
  "NICU/NALS(Tempo Traveller/Winger)": 0,
  "Hearse-Van": 0,
  "Air-Ambulance": 0,
  "Others": 0
};

const SectionCard = ({ id, title, icon: Icon, sectionRef, children }: any) => (
  <Box id={id} ref={sectionRef} bg="white" p={6} borderWidth={1} borderRadius="lg" shadow="sm" w="100%">
    <Flex align="center" mb={5} borderBottomWidth={1} pb={2}>
      {Icon && <Box as={Icon} size="22px" color="teal.600" mr={2} />}
      <Heading size="md" color="teal.600">{title}</Heading>
    </Flex>
    <VStack spacing={5} align="stretch">
      {children}
    </VStack>
  </Box>
);

function AmbulanceServiceFormStepOne({ onNext, providers, deleteProvider, clearFormOne, setClearFormOne }: any) {
  const toast = useToast();
  const [view, setView] = useState<'list' | 'form'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  
  // State variables for form fields
  const [formValues, setFormValues] = useState({
    name: '',
    addressLine1: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
    providerId: '',
    contactPersonName: '',
    availability: '24/7',
    notes: '',
    rateType: 'variable',
    baseRateNotes: '',
    currency: 'INR',
    paymentTerms: '',
    complianceCertified: false as boolean,
    complianceDoc: '',
    hygieneOk: false as boolean,
    gpsAvailable: false as boolean,
  });

  // State for dynamic fields//
  const [contacts, setContacts] = useState([{ countryCode: '+91', number: '', isPrimary: true, isWhatsApp: false }]);
  const [emails, setEmails] = useState(['']);
  const [coverageAreas, setCoverageAreas] = useState<any[]>([]);
  const [ambulanceTypes, setAmbulanceTypes] = useState<string[]>([]);
  const [fleet, setFleet] = useState<Record<string, number>>({ ...defaultFleet });
  const [manpower, setManpower] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [communicationMethods, setCommunicationMethods] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [eta, setEta] = useState<Record<string, { min: number; max: number }>>({});
  const [photos, setPhotos] = useState<string[]>(['']);

  const [error, setError] = useState({
    providerName: '',
    addressLine1: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
    mobile: "",
    email: ""
  });
  const [isEditFormClicked, setEditForm] = useState(false)
  //clear values
  const handleClear = () => {
    setFormValues({
      name: '',
      addressLine1: '',
      city: '',
      state: '', 
      country: '',
      providerId: "",
      pinCode: '',
      contactPersonName: '',
      availability: '24/7',
      notes: '',
      rateType: 'variable',
      baseRateNotes: '',
      currency: 'INR',
      paymentTerms: '',
      complianceCertified: false,
      complianceDoc: '',
      hygieneOk: false,
      gpsAvailable: false
    });
    setContacts([{ countryCode: '+91', number: '', isPrimary: true, isWhatsApp: false }]);
    setEmails([""]);
    setCoverageAreas([]);
    setAmbulanceTypes([]);
    setFleet({ ...defaultFleet });
    setManpower([]);
    setEquipment([]);
    setCommunicationMethods([]);
    setPaymentMethods([]);
    setEta({});
    setPhotos(['']);
  };

  // Handle input change for standard fields
  const handleChange = (e: any) => {
    let { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    if (name === 'name' && value) setError((prev) => ({ ...prev, providerName: "" }));
    if (name === 'pinCode' && value) setError((prev) => ({ ...prev, pinCode: "" }));
    if (name === 'addressLine1' && value) setError((prev) => ({ ...prev, addressLine1: "" }));
    if (name === 'state' && value) setError((prev) => ({ ...prev, state: "" }));
    if (name === 'city' && value) setError((prev) => ({ ...prev, city: "" }));
    if (name === 'country' && value) setError((prev) => ({ ...prev, country: "" }));
    if (name === "pinCode") {
      value = value.replace(/\D/g, "").slice(0, 6);
    }
    setFormValues((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : (name === 'pinCode' ? value : val) }));

  };

  // Handle dynamic field changes
  const handleContactChange = (index: number, field: string, value: any) => {
    if (field === 'number' && value) setError((prev) => ({ ...prev, mobile: "" }));

    const newContacts = contacts.map((contact, i) =>
      i === index ? {
        ...contact,
        [field]: field === 'number' ? value.replace(/\D/g, '').slice(0, 10) : value 
      } : contact
    );
    setContacts(newContacts);
  };

  const handleEmailChange = (index: number, value: string) => {
    // console.log('value:', value,index)
    if ( validateEmail(value)) setError((prev) => ({ ...prev, email: "" }));
    const newEmails = emails.map((email, i) => (i === index ? value : email));
    setEmails(newEmails);
  };

  const handleFleetChange = (type: string, value: string) => {
    setFleet((p) => ({ ...p, [type]: Number(value) || 0 }));
  };

  const handleEtaChange = (key: string, field: 'min' | 'max', value: string) => {
    setEta((p) => ({ ...p, [key]: { ...(p[key] || { min: 0, max: 0 }), [field]: Number(value) || 0 } }));
  };

  const handlePhotoChange = (index: number, value: string) => {
    setPhotos(photos.map((p, i) => (i === index ? value : p)));
  };

  const addPhoto = () => setPhotos([...photos, '']);
  const removePhoto = (index: number) => setPhotos(photos.filter((_, i) => i !== index));

  // Add and remove contact/email handlers
  const addContact = () => setContacts([...contacts, { countryCode: '+91', number: '', isPrimary: false, isWhatsApp: false }]);
  const removeContact = (index: number) => setContacts(contacts.filter((_, i) => i !== index));

  const addEmail = () => setEmails([...emails, '']);
  const removeEmail = (index: number) => setEmails(emails.filter((_, i) => i !== index));
  const validateMobile = (number: string) => {
    const phoneRegex = /^[0-9]{10}$/; // Regex for 10-digit phone numbers
    return phoneRegex.test(number);
  };

  // Email validation
  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/; // Regex for email format
    return emailRegex.test(email);
  };

  const preparePayload = (formErrors: any) => {
    const filteredEmails = emails.filter((e) => e.trim() !== '');
    if (filteredEmails.length > 0 && !filteredEmails.every(validateEmail)) {
      formErrors.email = 'Please provide valid email(s)';
      return null;
    }
    return filteredEmails.length > 0 ? { emails: filteredEmails } : {};
  };

  // Handle form submission
  const handleSubmit = () => {
    let formErrors = { ...error };
    let valid = true;
    if (!formValues.name) {
      formErrors.providerName = "Provider name is required";
      valid = false;
    }

    // Validate addressLine1
    if (!formValues.addressLine1) {
      formErrors.addressLine1 = "Address Line 1 is required";
      valid = false;
    }

    // Validate city
    if (!formValues.city) {
      formErrors.city = "City is required";
      valid = false;
    };

    // Validate state
    if (!formValues.state) {
      formErrors.state = "State is required";
      valid = false;
    };
    // Validate country
    if (!formValues.country) {
      formErrors.country = "Country is required";
      valid = false;
    };
    // Validate pinCode
    if (!formValues.pinCode || formValues.pinCode.length<6) {
      formErrors.pinCode = "Pin code is required";
      valid = false;
    };
    contacts.forEach((contact) => {
      if (!contact.number || !validateMobile(contact.number)) {
        formErrors.mobile = "Mobile is Required";
        valid = false;
      }
    });
    // console.log('emails:', emails.length);
    const emailResult=preparePayload(formErrors);
    if(emailResult ===null){
      valid=false;

    } 
    // console.log('formErrors:', formErrors,valid,validEmail);

    setError(formErrors);
    if (!valid) {
      const missingFields: string[] = [];
      if (formErrors.providerName) missingFields.push('Operator Name');
      if (formErrors.addressLine1) missingFields.push('Address Line 1');
      if (formErrors.city) missingFields.push('City');
      if (formErrors.state) missingFields.push('State');
      if (formErrors.country) missingFields.push('Country');
      if (formErrors.pinCode) missingFields.push('Pin Code');
      if (formErrors.mobile) missingFields.push('Mobile Number');
      if (formErrors.email) missingFields.push('Email');
      toast({
        title: 'Required fields missing',
        description: missingFields.join(', '),
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    const filteredPhotos = photos.filter((p) => p.trim() !== '');
    const filteredCoverage = coverageAreas.filter((a) => a.name.trim() !== '' && !a._id);
    const etaPayload: Record<string, { min: number; max: number }> = {};
    ambulanceTypes.forEach((t) => { if (eta[t]) etaPayload[t] = eta[t]; });
    if (eta['doctorArrival']) etaPayload['doctorArrival'] = eta['doctorArrival'];

    onNext(
      {
        ...formValues, contacts, emails: emailResult?.emails, coverageAreas: filteredCoverage,
        ambulanceTypes, fleet, manpower, equipment, communicationMethods,
        paymentMethods, photos: filteredPhotos, eta: etaPayload,
      },
      handleClear,
      isEditFormClicked ? 'edit' : 'create'
    );

    if (isEditFormClicked) setEditForm(false);
    setView('list');
  };

  const handleEdit = async (providerData: any) => {
    setEditForm(true);
    setView('form');
    setFormValues({
      name: providerData.operatorName || '',
      addressLine1: providerData.address?.addressLine1 || '',
      city: providerData.address?.city || '',
      state: providerData.address?.state || '',
      country: providerData.address?.country || '',
      pinCode: providerData.address?.pinCode || '',
      providerId: providerData._id,
      contactPersonName: providerData.contact?.personName || '',
      availability: providerData.availability || '24/7',
      notes: providerData.notes || '',
      rateType: providerData.pricing?.rateType || 'variable',
      baseRateNotes: providerData.pricing?.baseRateNotes || '',
      currency: providerData.pricing?.currency || 'INR',
      paymentTerms: providerData.payment?.terms || '',
      complianceCertified: providerData.complianceCertified || false,
      complianceDoc: providerData.complianceDoc || '',
      hygieneOk: providerData.hygieneOk || false,
      gpsAvailable: providerData.gpsAvailable || false,
    });

    // Set contacts (assuming mobile is an array of objects)
    setContacts(
      providerData.contact?.mobile?.length > 0
       ? providerData.contact.mobile.map((contact: any) => ({
      countryCode: contact.countryCode || '+91',
      number: contact.number || '',
      isPrimary: contact.isPrimary || false,
      isWhatsApp: contact.isWhatsApp || false,
    }))
    : [{ countryCode: '+91', number: '', isPrimary: true, isWhatsApp: false }]
  );

    // Set emails (assuming email is an array)
    setEmails(providerData.contact?.email || ['']);
    setCoverageAreas([]);
    setAmbulanceTypes(providerData.ambulanceTypes || []);
    setFleet(providerData.fleet || { ...defaultFleet });
    setManpower(providerData.manpower || []);
    setEquipment(providerData.equipment || []);
    setCommunicationMethods(providerData.communicationMethods || []);
    setPaymentMethods(providerData.payment?.methods || []);
    setEta(providerData.eta || {});
    setPhotos(providerData.photos?.map((p: any) => p.url) || ['']);
    const coverageRes = await fetchProviderCoverageAreas(providerData._id);
    const rawAreas = Array.isArray(coverageRes?.data) ? coverageRes.data
      : Array.isArray(coverageRes) ? coverageRes
      : [];
    setCoverageAreas(rawAreas.length > 0
      ? rawAreas.map((a: any) => ({ _id: a._id, name: a.name || '', rangeInKm: a.rangeInKm ?? 10, isHospital: a.isHospital || false, ambulancePlaced: a.ambulancePlaced || false }))
      : [{ name: '', rangeInKm: 10, isHospital: false, ambulancePlaced: false }]
    );
  };

  const handleDelete = async (providerData: any) => {
    //first need to get rate card id associated with provider and need to delete both provider and ambulance rate cards//
    // console.log('providerData._id:', providerData._id)
    try {

      await deleteProviderAndRateCard(providerData._id);
      // console.log('res:', res);
      deleteProvider();
    } catch (err) {
      deleteProvider();
      return
    }
    // if(res?.res&&res?.rateCardRes && res?.removeProviderFromCity){
    //   ShowAlert("success","Ambulance Provider and rate card deleted successfully");
    // }else if(!res?.rateCardRes){
    //   ShowAlert("warning","rate card not deleted");
    // }else if (!res.res){
    //   ShowAlert("warning","Ambulance Provider not deleted");
    // }else if(!res.removeProviderFromCity){
    //   ShowAlert("warning","Ambulance Cities not deleted");
    // };    
  }

  useEffect(() => {
    if (clearFormOne) {
      handleClear()
      setClearFormOne(!clearFormOne)
    }
    return;
    //eslint-disable-next-line
  }, [clearFormOne])

  const filteredProviders = providers?.filter((p: any) =>
    p.operatorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.address?.city?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const totalPages = Math.ceil(filteredProviders.length / PAGE_SIZE);
  const paginatedProviders = filteredProviders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearchChange = (e: any) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  if (view === 'list') {
    return (
      <Box p={6} bg="white" borderRadius="lg" shadow="sm" borderWidth={1}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md" color="teal.600">Saved Providers</Heading>
          <HStack spacing={4}>
            <Input
              placeholder="Search by name or city..."
              value={searchQuery}
              onChange={handleSearchChange}
              w="250px"
              bg="gray.50"
            />
            <Button colorScheme="teal" onClick={() => { handleClear(); setEditForm(false); setView('form'); }}>
              Add Provider
            </Button>
          </HStack>
        </Flex>
        <TableContainer>
          <Table variant="simple" colorScheme="teal">
            <Thead bg="gray.50">
              <Tr>
                <Th>Operator Name</Th>
                <Th>City</Th>
                <Th>Primary Contact</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedProviders.map((provider: any) => (
                <Tr key={provider._id} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="medium">{provider?.operatorName}</Td>
                  <Td>{provider?.address?.city}</Td>
                  <Td>{provider?.contact?.mobile[0]?.number || 'N/A'}</Td>
                  <Td>
                    <Flex justify="flex-end" gap={3}>
                      <Button size="sm" colorScheme="blue" variant="outline" onClick={() => handleEdit(provider)}>Edit</Button>
                      <Button size="sm" colorScheme="red" variant="ghost" onClick={() => handleDelete(provider)}>Delete</Button>
                    </Flex>
                  </Td>

                </Tr>
              ))}
              {paginatedProviders.length === 0 && (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={6} color="gray.500">No providers found.</Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        
        {totalPages > 1 && (
          <Flex justify="center" align="center" mt={4} gap={2}>
            <Button size="sm" onClick={() => setCurrentPage(p => p - 1)} isDisabled={currentPage === 1}>Prev</Button>
            {Array.from({ length: totalPages }, (_, i) => (
              <Button
                key={i + 1}
                size="sm"
                variant={currentPage === i + 1 ? 'solid' : 'outline'}
                colorScheme="teal"
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </Button>
            ))}
            <Button size="sm" onClick={() => setCurrentPage(p => p + 1)} isDisabled={currentPage === totalPages}>Next</Button>
          </Flex>
        )}
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minH="90vh" p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md" color="teal.600">{isEditFormClicked ? 'Edit Provider' : 'Add Provider'}</Heading>
        <Button variant="outline" onClick={() => { setView('list'); handleClear(); setEditForm(false); }}>Back to List</Button>
      </Flex>
      <Box as="form" mb={8}>
        <Grid templateColumns="repeat(2, 1fr)" gap={8} alignItems="start">
              
              {/* ===================== LEFT COLUMN ===================== */}
              <VStack spacing={6} w="100%">
                
                {/* 1. Basic Information */}
                <SectionCard title="Basic Information">
                  <FormControl isInvalid={!!error.providerName} isRequired>
                    <FormLabel>Operator Name</FormLabel>
                    <Input 
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    placeholder="Enter operator name"
                    bg="gray.50" 
                    />
                    {error.providerName && <FormErrorMessage>{error.providerName}</FormErrorMessage>}
                  </FormControl>

                  <FormControl isInvalid={!!error.addressLine1} isRequired>
                    <FormLabel>Address Line 1</FormLabel>
                    <Input name="addressLine1" value={formValues.addressLine1} onChange={handleChange} placeholder="Enter address line 1" bg="gray.50" />
                    {error.addressLine1 && <FormErrorMessage>{error.addressLine1}</FormErrorMessage>}
                  </FormControl>

                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl isInvalid={!!error.city} isRequired>
                      <FormLabel>City</FormLabel>
                      <Input name="city" value={formValues.city} onChange={handleChange} placeholder="City" bg="gray.50" />
                      {error.city && <FormErrorMessage>{error.city}</FormErrorMessage>}
                    </FormControl>
                    <FormControl isInvalid={!!error.state} isRequired>
                      <FormLabel>State</FormLabel>
                      <Input name="state" value={formValues.state} onChange={handleChange} placeholder="State" bg="gray.50" />
                      {error.state && <FormErrorMessage>{error.state}</FormErrorMessage>}
                    </FormControl>
                    <FormControl isInvalid={!!error.country} isRequired>
                      <FormLabel>Country</FormLabel>
                      <Input name="country" value={formValues.country} onChange={handleChange} placeholder="Country" bg="gray.50" />
                      {error.country && <FormErrorMessage>{error.country}</FormErrorMessage>}
                    </FormControl>
                    <FormControl isInvalid={!!error.pinCode} isRequired>
                      <FormLabel>PinCode</FormLabel>
                      <Input name="pinCode" value={formValues.pinCode} onChange={handleChange} placeholder="Pin code" bg="gray.50" />
                      {error.pinCode && <FormErrorMessage>{error.pinCode}</FormErrorMessage>}
                    </FormControl>
                  </SimpleGrid>
                </SectionCard>



                {/* 2. Coverage Areas */}
                <Box bg="white" p={6} borderWidth={1} borderRadius="lg" shadow="sm" w="100%">
                  <Flex align="center" justify="space-between" mb={2} borderBottomWidth={1} pb={2}>
                    <Flex align="center">
                      <Box as={MdMap} size="22px" color="teal.600" mr={2} />
                      <Heading size="md" color="teal.600">Coverage Areas</Heading>
                    </Flex>
                  </Flex>
                  <Text fontSize="sm" color="gray.500" mb={3}>Add coverage areas after saving the provider, or manage existing ones from the <strong>Coverage Areas</strong> tab above.</Text>

                </Box>

                {/* 3. Operations */}
                <SectionCard title="Operations & Capabilities">
                  <FormControl>
                    <FormLabel fontWeight="bold">Ambulance Types Supported</FormLabel>
                    <CheckboxGroup value={ambulanceTypes} onChange={(val) => setAmbulanceTypes(val as string[])} colorScheme="teal">
                      <Wrap spacing={4}>
                        {AMBULANCE_TYPES.map((t) => <WrapItem key={t}><Checkbox value={t}>{t}</Checkbox></WrapItem>)}
                      </Wrap>
                    </CheckboxGroup>
                  </FormControl>

                  {ambulanceTypes.length > 0 && (
                    <Box pt={4}>
                      <FormLabel fontWeight="bold">Available Fleet Count</FormLabel>
                      <SimpleGrid columns={3} spacing={4}>
                        {ambulanceTypes.map((t) => (
                          <FormControl key={t}>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>{t}</FormLabel>
                            <NumberInput min={0} value={fleet[t] || 0} onChange={(val) => handleFleetChange(t, val)} bg="gray.50">
                              <NumberInputField />
                            </NumberInput>
                          </FormControl>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}

                  <Divider />

                  <FormControl>
                    <FormLabel fontWeight="bold">Manpower Provided</FormLabel>
                    <CheckboxGroup value={manpower} onChange={(val) => setManpower(val as string[])} colorScheme="teal">
                      <Wrap spacing={4}>
                        {MANPOWER_OPTIONS.map((m) => <WrapItem key={m}><Checkbox value={m}>{m}</Checkbox></WrapItem>)}
                      </Wrap>
                    </CheckboxGroup>
                  </FormControl>

                  <FormControl mt={2}>
                    <FormLabel fontWeight="bold">Equipment</FormLabel>
                    <CheckboxGroup value={equipment} onChange={(val) => setEquipment(val as string[])} colorScheme="teal">
                      <Wrap spacing={4}>
                        {EQUIPMENT_OPTIONS.map((eq) => <WrapItem key={eq}><Checkbox value={eq}>{eq}</Checkbox></WrapItem>)}
                      </Wrap>
                    </CheckboxGroup>
                  </FormControl>
                </SectionCard>

                {/* 4. Logistics & ETA */}
                <SectionCard title="Service Logistics & ETA">
                  <Text fontWeight="bold" mb={2}>Estimated Time of Arrival (Mins)</Text>
                  <SimpleGrid columns={2} spacing={6}>
                    {[...ambulanceTypes, ...(manpower.includes('Doctor') || manpower.includes('Doctor on Call') ? ['doctorArrival'] : [])].map((key) => (
                      <Box key={key} p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                        <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
                          {key === 'doctorArrival' ? 'Doctor Arrival' : key}
                        </Text>
                        <HStack>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Min</FormLabel>
                            <NumberInput min={0} value={eta[key]?.min || 0} onChange={(val) => handleEtaChange(key, 'min', val)} size="sm" bg="white">
                              <NumberInputField />
                            </NumberInput>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Max</FormLabel>
                            <NumberInput min={0} value={eta[key]?.max || 0} onChange={(val) => handleEtaChange(key, 'max', val)} size="sm" bg="white">
                              <NumberInputField />
                            </NumberInput>
                          </FormControl>
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>

                  <Divider />

                  <FormControl>
                    <FormLabel fontWeight="bold">Communication Methods</FormLabel>
                    <CheckboxGroup value={communicationMethods} onChange={(val) => setCommunicationMethods(val as string[])} colorScheme="teal">
                      <Wrap spacing={4}>
                        {COMM_OPTIONS.map((c) => <WrapItem key={c}><Checkbox value={c}>{c}</Checkbox></WrapItem>)}
                      </Wrap>
                    </CheckboxGroup>
                  </FormControl>

                </SectionCard>

              </VStack>

              {/* ===================== RIGHT COLUMN ===================== */}
              <VStack spacing={6} w="100%">

                {/* 1. Contact Details */}
                <SectionCard title="Contact Details">
                  <FormControl>
                    <FormLabel>Contact Person Name</FormLabel>
                    <Input name="contactPersonName" value={formValues.contactPersonName} onChange={handleChange} placeholder="Name of primary contact" bg="gray.50" />
                  </FormControl>

                  <Divider />
                  
                  <Box>
                    <FormLabel fontWeight="bold">Mobile Numbers</FormLabel>
                    <VStack spacing={3} align="stretch">
                      {contacts.map((contact, index) => (
                        <HStack key={index} spacing={4} alignItems="center" bg="gray.50" p={2} borderRadius="md" borderWidth={1}>
                          <FormControl isInvalid={!!error.mobile} isRequired flex={1}>
                            <Input value={contact.number} onChange={(e) => handleContactChange(index, 'number', e.target.value)} placeholder="10-digit number" bg="white" />
                          </FormControl>
                          <Checkbox isChecked={contact.isPrimary} onChange={(e) => handleContactChange(index, 'isPrimary', e.target.checked)} colorScheme="teal">Primary</Checkbox>
                          <Checkbox isChecked={contact.isWhatsApp} onChange={(e) => handleContactChange(index, 'isWhatsApp', e.target.checked)} colorScheme="green">WhatsApp</Checkbox>
                          {index !== 0 && (
                            <IconButton icon={<MdDelete />} onClick={() => removeContact(index)} aria-label="Remove contact" colorScheme="red" variant="ghost" size="sm" />
                          )}
                        </HStack>
                      ))}
                      <Button leftIcon={<MdAdd />} onClick={addContact} size="sm" colorScheme="teal" variant="outline" alignSelf="flex-start">Add Number</Button>
                    </VStack>
                  </Box>

                  <Box>
                    <FormLabel fontWeight="bold">Email Addresses</FormLabel>
                    <VStack spacing={3} align="stretch">
                      {emails.map((email, index) => (
                        <HStack key={index} spacing={4}>
                          <FormControl isInvalid={!!error.email} flex={1}>
                            <Input value={email} onChange={(e) => handleEmailChange(index, e.target.value)} placeholder="Email address" bg="gray.50" />
                            {error.email && <FormErrorMessage>{error.email}</FormErrorMessage>}
                          </FormControl>
                          {index !== 0 && (
                            <IconButton icon={<MdDelete />} onClick={() => removeEmail(index)} aria-label="Remove email" colorScheme="red" variant="ghost" />
                          )}
                        </HStack>
                      ))}
                      <Button leftIcon={<MdAdd />} onClick={addEmail} size="sm" colorScheme="teal" variant="outline" alignSelf="flex-start">Add Email</Button>
                    </VStack>
                  </Box>
                </SectionCard>

                {/* 2. Financials & Billing */}
                <SectionCard title="Financials & Billing">
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>Rate Type</FormLabel>
                      <Select name="rateType" value={formValues.rateType} onChange={handleChange} bg="gray.50">
                        <option value="variable">Variable</option>
                        <option value="fixed">Fixed</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Currency</FormLabel>
                      <Input name="currency" value={formValues.currency} onChange={handleChange} placeholder="INR" bg="gray.50" />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Base Rate Notes</FormLabel>
                    <Textarea name="baseRateNotes" value={formValues.baseRateNotes} onChange={handleChange} placeholder="Describe base rate terms..." bg="gray.50" rows={2} />
                  </FormControl>

                  <Divider />

                  <FormControl>
                    <FormLabel fontWeight="bold">Accepted Payment Methods</FormLabel>
                    <CheckboxGroup value={paymentMethods} onChange={(val) => setPaymentMethods(val as string[])} colorScheme="teal">
                      <Wrap spacing={4}>
                        {PAYMENT_OPTIONS.map((p) => <WrapItem key={p}><Checkbox value={p}>{p}</Checkbox></WrapItem>)}
                      </Wrap>
                    </CheckboxGroup>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Payment Terms</FormLabel>
                    <Select name="paymentTerms" value={formValues.paymentTerms} onChange={handleChange} bg="gray.50">
                      <option value="">Select Payment Term</option>
                      <option value="Trip Start">Trip Start</option>
                      <option value="Trip End">Trip End</option>
                      <option value="Flexible">Flexible</option>
                    </Select>
                  </FormControl>
                </SectionCard>

                {/* 3. Additional Details */}
                <SectionCard title="Additional Details">
                  <FormControl>
                    <FormLabel>General Availability</FormLabel>
                    <Select name="availability" value={formValues.availability} onChange={handleChange} bg="gray.50">
                      <option value="Day">Day Only</option>
                      <option value="Night">Night Only</option>
                      <option value="24/7">24/7 Operations</option>
                    </Select>
                  </FormControl>

                  <HStack spacing={8} pt={2} pb={2}>
                    <Checkbox name="complianceCertified" isChecked={formValues.complianceCertified} onChange={handleChange} colorScheme="teal">Compliance Certified</Checkbox>
                    <Checkbox name="hygieneOk" isChecked={formValues.hygieneOk} onChange={handleChange} colorScheme="teal">Hygiene Certified</Checkbox>
                    <Checkbox name="gpsAvailable" isChecked={formValues.gpsAvailable} onChange={handleChange} colorScheme="teal">GPS Available</Checkbox>
                  </HStack>

                  <Divider />

                  <FormControl>
                    <FormLabel fontWeight="bold">Compliance Document Link</FormLabel>
                    <Input name="complianceDoc" value={formValues.complianceDoc} onChange={handleChange} placeholder="https://docs.example.com/compliance.pdf" bg="gray.50" />
                  </FormControl>

                  <Divider />

                  <Box>
                    <FormLabel fontWeight="bold">Ambulance Photo Links</FormLabel>
                    <VStack spacing={3} align="stretch">
                      {photos.map((photo, index) => (
                        <HStack key={index} spacing={3}>
                          <FormControl flex={1}>
                            <Input value={photo} onChange={(e) => handlePhotoChange(index, e.target.value)} placeholder="https://image-url.com/photo.jpg" bg="gray.50" />
                          </FormControl>
                          {index !== 0 && (
                            <IconButton icon={<MdDelete />} onClick={() => removePhoto(index)} aria-label="Remove photo" colorScheme="red" variant="ghost" />
                          )}
                        </HStack>
                      ))}
                      <Button leftIcon={<MdAdd />} onClick={addPhoto} size="sm" colorScheme="teal" variant="outline" alignSelf="flex-start">Add Photo URL</Button>
                    </VStack>
                  </Box>

                  <Divider />

                  <FormControl>
                    <FormLabel>General Notes</FormLabel>
                    <Textarea name="notes" value={formValues.notes} onChange={handleChange} placeholder="Any other specific details regarding this operator..." bg="gray.50" rows={3} />
                  </FormControl>
                </SectionCard>

              </VStack>
            </Grid>

        {/* Submit Bar Fixed at bottom of form content */}
        <Flex mt={8} justify="flex-end" bg="white" p={4} borderRadius="lg" shadow="sm" borderWidth={1}>
          <Button onClick={handleSubmit} colorScheme="blue" size="lg" px={10}>
            {isEditFormClicked ? 'Save Updates' : 'Save Provider'}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}

export default AmbulanceServiceFormStepOne;
